import React from "react";
import { Phone, Mail, MapPin, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { COMPANY, buildWhatsAppUrl } from "@/lib/constants";

const ContactMap = () => {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 lg:py-28 bg-[#F8FAFC]">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-12 items-stretch">
          <div>
            <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#19A8E8]">Get In Touch</div>
            <h2 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl lg:text-5xl text-[#0F172A] tracking-tight leading-tight">
              Talk to a real <br />
              <span className="text-[#19A8E8]">booking expert</span>.
            </h2>
            <p className="mt-4 text-[#64748B] max-w-lg leading-relaxed">
              We're available round-the-clock. Pick the channel you prefer — phone, WhatsApp or email. We'll respond within minutes.
            </p>

            <div className="mt-8 space-y-3">
              <a href={`tel:${COMPANY.phoneTel}`} data-testid="contact-phone-card"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#19A8E8] hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition-all">
                <div className="h-12 w-12 rounded-xl bg-[#19A8E8] text-white flex items-center justify-center group-hover:rotate-6 transition-transform">
                  <Phone className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Phone (24/7)</div>
                  <div className="font-display font-bold text-lg text-[#0F172A]">{COMPANY.phoneDisplay}</div>
                </div>
              </a>

              <a href={buildWhatsAppUrl("Hi Cabsab.com, I'd like to book a cab.")} target="_blank" rel="noopener noreferrer"
                data-testid="contact-whatsapp-card"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-emerald-400 hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition-all">
                <div className="h-12 w-12 rounded-xl bg-emerald-500 text-white flex items-center justify-center group-hover:rotate-6 transition-transform">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">WhatsApp</div>
                  <div className="font-display font-bold text-lg text-[#0F172A]">Chat with us instantly</div>
                </div>
              </a>

              <a href={`mailto:${COMPANY.email}`} data-testid="contact-email-card"
                className="group flex items-center gap-4 p-5 rounded-2xl border border-slate-200 bg-white hover:border-[#19A8E8] hover:shadow-[0_12px_30px_rgba(15,23,42,0.06)] transition-all">
                <div className="h-12 w-12 rounded-xl bg-[#19A8E8]/10 text-[#19A8E8] flex items-center justify-center group-hover:rotate-6 transition-transform">
                  <Mail className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="text-xs uppercase tracking-wider text-[#64748B] font-semibold">Email</div>
                  <div className="font-display font-bold text-lg text-[#0F172A]">{COMPANY.email}</div>
                </div>
              </a>
            </div>
          </div>

          <div data-testid="contact-map-placeholder"
            className="relative rounded-3xl overflow-hidden border border-slate-200 shadow-[0_20px_50px_rgba(15,23,42,0.08)] min-h-[420px] bg-white">
            <iframe title="Cabsab Service Area"
              src="https://www.openstreetmap.org/export/embed.html?bbox=76.5,30.5,77.0,30.9&layer=mapnik&marker=30.7333,76.7794"
              className="absolute inset-0 w-full h-full" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
            <div className="absolute top-5 left-5 right-5 flex items-start justify-between gap-3 pointer-events-none">
              <div className="bg-white rounded-2xl px-4 py-3 shadow-md border border-slate-100 pointer-events-auto">
                <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold text-[#19A8E8]">
                  <MapPin className="h-3.5 w-3.5" />
                  Service Headquarters
                </div>
                <div className="font-display font-bold text-base text-[#0F172A] mt-1">{COMPANY.address}</div>
                <div className="text-xs text-[#64748B]">Serving Across 17 Cities</div>
              </div>
            </div>
            <div className="absolute bottom-5 left-5 right-5 pointer-events-none flex justify-end">
              <Button asChild data-testid="contact-book-whatsapp-btn"
                className="rounded-full bg-[#19A8E8] hover:bg-[#0E8AC4] text-white font-semibold shadow-[0_8px_24px_rgba(25,168,232,0.30)] pointer-events-auto">
                <a href={buildWhatsAppUrl("Hi Cabsab.com, I'd like to book a cab.")} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="h-4 w-4 mr-2" /> Book on WhatsApp
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactMap;
